<!--
  @component ProjectCarouselBlock — the horizontal "your projects" row.
  tier:      home block (kind: "project-carousel")
  purpose:   browse projects visually (scroll-snap tiles) + a New-project tile.
-->
<script lang="ts">
  import { push } from "svelte-spa-router";
  import { useIdentityIO } from "../../accounts/IdentityIO.svelte";
  import { useProvenanceIO } from "../../../../global/io/nodes/deployment/ProvenanceIO.svelte";
  import { useHomeActions } from "../home-context.js";
  import { projectRollup } from "../home-util.js";
  import ProjectTile from "../ProjectTile.svelte";

  const identity = useIdentityIO();
  const provenance = useProvenanceIO();
  const actions = useHomeActions();

  let scroller: HTMLDivElement | null = $state(null);
  const roll = $derived(projectRollup(provenance.events));
  const sorted = $derived(
    [...identity.projects].sort((a, b) => (b.createdAtMs ?? 0) - (a.createdAtMs ?? 0)),
  );

  function nudge(dir: number): void {
    scroller?.scrollBy({ left: dir * 288, behavior: "smooth" });
  }
</script>

<section class="block">
  <header class="bh">
    <h2>Your projects</h2>
    <div class="ctrls">
      <button type="button" onclick={() => nudge(-1)} aria-label="Scroll left">‹</button>
      <button type="button" onclick={() => nudge(1)} aria-label="Scroll right">›</button>
    </div>
  </header>

  <div class="scroller" bind:this={scroller}>
    {#each sorted as p (p.id)}
      {@const r = roll.get(p.id)}
      <div class="cell">
        <ProjectTile
          project={p}
          status={r?.status ?? "idle"}
          lastActivityMs={r?.lastActivityMs}
          onClick={() => push(`/projects/${p.id}/overview`)}
        />
      </div>
    {/each}
    <button type="button" class="add cell" onclick={() => actions.openNewProject()}>
      <span class="add-plus" aria-hidden="true">+</span>
      <span>New project</span>
    </button>
  </div>
</section>

<style>
  .block { display: flex; flex-direction: column; gap: var(--space-3); font-family: var(--type-font); }
  .bh { display: flex; align-items: center; justify-content: space-between; gap: var(--space-3); }
  .bh h2 { margin: 0; font-size: 15px; font-weight: 600; letter-spacing: -0.01em; color: var(--text-primary); }
  .ctrls { display: flex; gap: var(--space-1); }
  .ctrls button {
    width: 28px; height: 28px; display: grid; place-items: center;
    background: var(--glass-fill); border: 0.5px solid var(--border); border-radius: var(--radius-md);
    color: var(--text-secondary); font-size: 15px; line-height: 1; cursor: pointer;
    transition: background var(--ease-fast), color var(--ease-fast), border-color var(--ease-fast);
  }
  .ctrls button:hover { background: var(--bg-hover); color: var(--text-primary); border-color: var(--border-strong); }

  .scroller {
    display: flex;
    gap: var(--space-3);
    overflow-x: auto;
    padding: 4px 2px var(--space-2);
    scroll-snap-type: x mandatory;
    scrollbar-width: none;
    -webkit-mask-image: linear-gradient(90deg, transparent 0, #000 18px, #000 calc(100% - 26px), transparent 100%);
    mask-image: linear-gradient(90deg, transparent 0, #000 18px, #000 calc(100% - 26px), transparent 100%);
  }
  .scroller::-webkit-scrollbar { display: none; }
  .cell { flex: 0 0 264px; scroll-snap-align: start; }

  .add {
    display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px;
    min-height: 188px;
    background: transparent; border: 1px dashed var(--border-strong); border-radius: var(--radius-lg, 14px);
    color: var(--text-tertiary); font: inherit; font-size: 13px; font-weight: 500; cursor: pointer;
    transition: color var(--ease-fast), border-color var(--ease-fast), background var(--ease-fast);
  }
  .add:hover { color: var(--text-primary); border-color: var(--border-focus); background: var(--bg-hover); }
  .add-plus { font-size: 26px; font-weight: 300; line-height: 1; }
</style>
